import { Pencil } from "lucide-react";
import type { FC } from "react";
import { Link } from "react-router-dom";

import type { BranchUnderGroup } from "@/api/admin/groups";
import { CustomBadge, CustomButton, Switch } from "@/components/atoms";
import { cn } from "@/lib/utils";
import { commonIconProps20 } from "@/utils/constants";
import { wordToUpper } from "@/utils/wordToUpper";

import { BasicModal } from "../BasicModal";

interface BrandFieldEditProps {
  name: string;
  list?: BranchUnderGroup[];
  editClik?: () => void;
  isTrash?: boolean;
  classes?: string;
  removeClick?: () => void;
  state?: string;
  isPending?: boolean;
  isActive?: boolean;
  linkToEntity?: string;
  disabled?: boolean;
  hasUpdatePermission?: boolean;
}

const BrandFieldEdit: FC<BrandFieldEditProps> = ({
  name,
  list,
  isTrash = false,
  classes,
  editClik,
  removeClick,
  state = "",
  isPending = false,
  isActive = true,
  linkToEntity,
  disabled,
  hasUpdatePermission = true,
}) => {
  return (
    <li
      className={cn(
        "flex justify-between px-3.5 py-4 items-center border-b border-gray-200 last:border-b-0",
        classes
      )}
    >
      <div className={cn("flex flex-col", list && list.length > 0 && "gap-2")}>
        {linkToEntity ? (
          <Link to={linkToEntity} className="font-bold text-sm">
            {name}
          </Link>
        ) : (
          <p className="font-bold text-sm">{name}</p>
        )}

        {list && (
          <ul className="flex items-center gap-2 flex-wrap">
            {list.map(elem => (
              <li key={elem.branchId}>
                <CustomBadge>{elem.branchName}</CustomBadge>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="flex items-center">
        {isTrash && (
          <BasicModal
            trigger={
              <Switch
                id="active"
                checked={isActive}
                data-testid="switch"
                disabled={disabled}
                className="w-[30px] h-4 [&>span]:w-[12px] [&>span]:h-[12px] [&>span]:data-[state=checked]:translate-x-[14px]"
              />
            }
            title={`${isActive ? "Deactivate" : "Activate"} ${wordToUpper(state)}`}
            body={
              <p className="text-sm text-gray-950 mb-2">
                Are you sure you want to
                {isActive ? " deactivate" : " activate"}
                <span className="font-bold pl-1">{name}</span> {state}?
              </p>
            }
            action={() => removeClick?.()}
            classes="mx-4"
            btnTitle={isActive ? "Deactivate" : "Activate"}
            isPending={isPending}
          />
        )}

        <CustomButton
          variant="icon"
          onClick={editClik}
          type="button"
          className="mr-2"
          data-testid={`edit-pencil-button-${name}`}
          disabled={!hasUpdatePermission}
        >
          {hasUpdatePermission && <Pencil {...commonIconProps20} />}
        </CustomButton>
      </div>
    </li>
  );
};

export default BrandFieldEdit;
